import { useEffect, useMemo, useState } from 'react';
import { formatWholeNumber } from '../game/util/numberFormat';

const paddedDigits = (value, width) => {
  const digits = String(Math.abs(value)).split('');
  return Array.from({ length: width }, (_, index) => {
    const sourceIndex = index - (width - digits.length);
    return sourceIndex >= 0 ? digits[sourceIndex] : '';
  });
};

function SubtractionNumberLineVisual({ tutorial, step }) {
  const maximum = tutorial.numberLineMax || 20;
  const start = tutorial.a;
  const result = tutorial.result;
  const lineStart = 62;
  const lineEnd = 938;
  const baseline = 132;
  const pointX = value => lineStart + ((lineEnd - lineStart) * value / maximum);
  const jumps = step.showJumps
    ? Array.from({ length: tutorial.b }, (_, index) => ({
      from: start - index,
      to: start - index - 1,
      label: index + 1,
    }))
    : [];

  return (
    <div className="tmag-number-line" data-step={step.id}>
      <div className="tmag-number-line-equation" aria-label={tutorial.a + ' menos ' + tutorial.b + ' es ' + (step.showResult ? result : 'incógnita')}>
        <span className="tmag-number-line-expression-start">{formatWholeNumber(tutorial.a)}</span>
        <span className="tmag-number-line-expression-operator"> − </span>
        <span className="tmag-number-line-expression-move">{formatWholeNumber(tutorial.b)}</span>
        <span className="tmag-number-line-expression-operator"> = </span>
        <strong className={step.showResult ? 'is-resolved' : ''}>{step.showResult ? formatWholeNumber(result) : '?'}</strong>
      </div>

      <div className="tmag-number-line-stage" aria-label={'Recta numérica de 0 a ' + maximum}>
        <svg className="tmag-number-line-svg" viewBox="0 0 1000 200" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          {jumps.map(jump => {
            const from = pointX(jump.from);
            const to = pointX(jump.to);
            const middle = (from + to) / 2;
            return (
              <g key={jump.from + '-' + jump.to}>
                <path
                  className="tmag-number-line-arc"
                  d={'M ' + from + ' 123 Q ' + middle + ' 76 ' + to + ' 123'}
                />
                <text className="tmag-number-line-jump-label" x={middle} y="64" textAnchor="middle">
                  {jump.label}
                </text>
              </g>
            );
          })}

          <line className="tmag-number-line-base" x1={lineStart} y1={baseline} x2={lineEnd} y2={baseline} />
          <path
            className="tmag-number-line-end-arrow"
            d={'M ' + (lineStart - 26) + ' ' + baseline + ' L ' + lineStart + ' ' + (baseline - 16) + ' L ' + lineStart + ' ' + (baseline + 16) + ' Z'}
          />
          <path
            className="tmag-number-line-end-arrow"
            d={'M ' + (lineEnd + 26) + ' ' + baseline + ' L ' + lineEnd + ' ' + (baseline - 16) + ' L ' + lineEnd + ' ' + (baseline + 16) + ' Z'}
          />

          {Array.from({ length: maximum + 1 }, (_, value) => {
            const x = pointX(value);
            const traversed = step.showJumps && value < start && value >= result;
            const isStart = value === start;
            const isResult = step.showResult && value === result;
            const classes = [
              isStart ? 'is-start' : '',
              traversed ? 'is-traversed' : '',
              isResult ? 'is-result' : '',
            ].filter(Boolean).join(' ');

            return (
              <g className={classes} key={value}>
                <line className="tmag-number-line-tick" x1={x} y1="114" x2={x} y2="150" />
                {isStart ? <circle className="tmag-number-line-start-marker" cx={x} cy={baseline} r="17" /> : null}
                {isResult ? <circle className="tmag-number-line-result-marker" cx={x} cy={baseline} r="19" /> : null}
                <text className="tmag-number-line-label" x={x} y="178" textAnchor="middle">{value}</text>
              </g>
            );
          })}
        </svg>
      </div>

      {step.id === 'start' ? (
        <p className="tmag-number-line-caption">
          {'El punto de inicio es ' + formatWholeNumber(start) + '.'}
        </p>
      ) : step.id === 'count' && tutorial.b > 0 ? (
        <p className="tmag-number-line-caption">
          {'Damos ' + formatWholeNumber(tutorial.b) + ' ' + (tutorial.b === 1 ? 'salto' : 'saltos') + ' a la izquierda desde el ' + formatWholeNumber(start) + '.'}
        </p>
      ) : null}
    </div>
  );
}

function SubtractionColumnCalculation({ tutorial, step }) {
  const topDigits = step.minuendDigits || paddedDigits(tutorial.a, tutorial.gridWidth);
  const bottomDigits = paddedDigits(tutorial.b, tutorial.gridWidth);
  const resultDigits = paddedDigits(tutorial.result, tutorial.gridWidth);
  const visible = new Set(step.resultVisible || []);
  const regrouped = new Set(step.regroupedColumns || []);

  return (
    <div className="tmag-worked-columns tmag-worked-subtraction-columns" style={{ '--tmag-columns': tutorial.gridWidth }}>
      <div className="tmag-worked-row">
        {topDigits.map((digit, index) => {
          const classes = [
            step.activeColumn === index ? 'is-active' : '',
            regrouped.has(index) ? 'is-regrouped' : '',
          ].filter(Boolean).join(' ');
          return <span className={classes} key={'a-' + index}>{digit}</span>;
        })}
      </div>
      <div className="tmag-worked-row tmag-worked-subtrahend">
        {bottomDigits.map((digit, index) => (
          <span className={step.activeColumn === index ? 'is-active' : ''} key={'b-' + index}>{digit}</span>
        ))}
      </div>
      <div className="tmag-worked-line" />
      <div className="tmag-worked-row tmag-worked-result">
        {resultDigits.map((digit, index) => (
          <span className={step.activeColumn === index ? 'is-active' : ''} key={'r-' + index}>
            {visible.has(index) ? digit : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

export function SubtractionWorkedExampleTutorial({ tutorial, onComplete }) {
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    const lastStep = Math.max(0, (tutorial?.tutorial?.steps?.length || 1) - 1);
    const requestedStep = Number.isInteger(tutorial?.initialStepIndex)
      ? tutorial.initialStepIndex
      : 0;
    setStepIndex(Math.min(Math.max(0, requestedStep), lastStep));
  }, [tutorial?.sessionId]);

  const step = tutorial?.tutorial?.steps?.[stepIndex];
  const finalStep = Boolean(tutorial && stepIndex === tutorial.tutorial.steps.length - 1);
  const progress = useMemo(
    () => tutorial ? `${stepIndex + 1} de ${tutorial.tutorial.steps.length}` : '',
    [tutorial, stepIndex],
  );

  if (!tutorial || !step) return null;

  return (
    <section className="tmag-worked-overlay tmag-subtraction-theme" role="dialog" aria-modal="true" aria-labelledby="subtraction-worked-example-title">
      <div className="tmag-worked-dialog">
        <header className="tmag-worked-header">
          <span className="tmag-worked-orb" aria-hidden="true">✦</span>
          <div>
            <p>{progress}</p>
            <h2 id="subtraction-worked-example-title">VEAMOS CÓMO SE HACE</h2>
          </div>
        </header>

        <div className="tmag-worked-step">
          <span className="tmag-worked-step-badge">{step.label}</span>
          <h3>{step.title}</h3>
          <p>{step.message}</p>
          {tutorial.tutorial.kind === 'columns-subtraction'
            ? <SubtractionColumnCalculation tutorial={tutorial.tutorial} step={step} />
            : <SubtractionNumberLineVisual tutorial={tutorial.tutorial} step={step} />}
        </div>

        <button
          className={'tmag-worked-next' + (finalStep ? ' is-final' : '')}
          type="button"
          onClick={() => finalStep ? onComplete() : setStepIndex(current => current + 1)}
        >
          {finalStep ? 'SEGUIR PRACTICANDO  →' : 'SIGUIENTE  →'}
        </button>
      </div>
    </section>
  );
}

export default SubtractionWorkedExampleTutorial;
